/**
 * Resolve user-facing copy for a newsletter episode + its sections
 * based on the active UI locale. Same shape and fallback rules as
 * localizedApp / localizedPost: per-locale overrides live in the
 * row's `translations` JSON, with the canonical columns (title,
 * teaser, …) as the last-resort fallback.
 *
 * Order of precedence for each field:
 *   translations[locale][field]  →  translations.fr[field]  →  row[field]
 *
 * So an episode that was authored in FR before the merge migration
 * still renders correctly for FR readers (no translations needed),
 * and an NL/EN reader sees the FR copy until a translation is added.
 */
function pick(row, locale, field) {
  // FR is the canonical · the editor writes FR-tab edits to the
  // top-level field (`row[field]`) and leaves `translations.fr`
  // alone, so reading from translations.fr first would return a
  // stale snapshot (older save). Always read top-level for FR;
  // NL/EN read their translations slot with a fallback to the FR
  // canonical when the locale slot is empty.
  if (locale === 'fr') return row?.[field] || '';
  const tr = row?.translations || {};
  const wanted = tr[locale] || {};
  if (wanted[field] != null && String(wanted[field]).trim() !== '') return wanted[field];
  return row?.[field] || '';
}

export function localizedEpisode(ep, locale) {
  if (!ep) return { title: '', teaser: '', date_label: '' };
  return {
    title:      pick(ep, locale, 'title'),
    teaser:     pick(ep, locale, 'teaser'),
    date_label: pick(ep, locale, 'date_label'),
  };
}

/* An upcoming event · same rule: FR canonical, NL / EN from `translations`. */
export function localizedEvent(ev, locale) {
  if (!ev) return { title: '', date_label: '', location: '', description: '' };
  return {
    title:       pick(ev, locale, 'title'),
    date_label:  pick(ev, locale, 'date_label'),
    location:    pick(ev, locale, 'location'),
    description: pick(ev, locale, 'description'),
  };
}

export function localizedSection(sec, locale) {
  if (!sec) return { title: '', body_markdown: '', items: [] };
  return {
    title:         pick(sec, locale, 'title'),
    body_markdown: pick(sec, locale, 'body_markdown'),
    items:         localizedItems(sec, locale),
  };
}

/* Bullet items used to live in `data.items` (shared across locales),
 * so a French-authored list stayed in French on the NL/EN page. Now
 * we accept per-locale items in `translations[locale].items` first,
 * fall back to FR translations.items, then to the legacy `data.items`
 * so existing rows keep working unchanged. */
export function localizedItems(sec, locale) {
  // Blank lines survive in the editor's draft while typing (a controlled
  // textarea needs them for Enter to work) · they never render.
  const clean = (arr) => (Array.isArray(arr) ? arr.filter(s => (s || '').trim() !== '') : []);
  const tr = sec?.translations || {};
  const wantedArr = clean(tr[locale]?.items);
  if (wantedArr.length) return wantedArr;
  const frArr = clean(tr.fr?.items);
  if (frArr.length) return frArr;
  return clean(sec?.data?.items);
}
